import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import {
  PieChart,
  XYChart,
  LabelBullet,
  PieChart3D,
  XYChart3D,
  CircleBullet,
  GaugeChart
} from '@amcharts/amcharts4/charts';
import * as am4plugins_sliceGrouper from '@amcharts/amcharts4/plugins/sliceGrouper';
import Grid from '@mui/material/Grid';
import { Base64 } from 'js-base64';

import { VisualSettings } from '@DStypes/chart';

import {
  formatChartAxisTitle,
  formatTitle,
  roundDecimal,
  roundToNearestMagnitude
} from '@utils/common';
import { DATE_GROUP, DATE_TIME_GROUP, DATE_GROUP_WITHOUT_FORMAT } from '@constants/common';
import {
  am4core,
  getBaseInterval,
  getDateFormatForTooltip,
  getDayJsUnitString,
  getYearMonthFormat
} from '@utils/chart';
import MDBox from '@components/MDBox';
import MDTypography from '@components/MDTypography';
import { RootState } from '@store/store';
import { THEME_STYLES } from '@constants/theme';
import breakpoints from '@theme/base/breakpoints';
import dayjs from '@utils/dayjs';

import './styles.scss';
import { CircularProgress, Tooltip } from '@mui/material';
import brandLogo from '@assets/images/ds-watermark-img.png';
import Watermark from '@components/Watermark';
import { sendAmplitudeData } from '@utils/amplitude';
import { CLICKED_CHART_AREA } from '@constants/eventNames';
import colors from '@theme/base/colors';

const { createFromConfig } = am4core;

// Fool the tree-shaking
am4plugins_sliceGrouper;

interface Props {
  chartViewRef?: any;
  chartId: string;
  data: any;
  chartConfig: any;
  width?: string;
  height?: string;
  chartType?: string;
  visualSettings?: VisualSettings | null;
  handleOnChartReady?: () => void;
  hideAxisLabels?: boolean;
  isPreview?: boolean;
  isProjectBoard?: boolean;
  colorSet: string[] | null;
  dateGroup?: DATE_GROUP | '' | 'none' | DATE_TIME_GROUP;
  getSvg?: (svg: string, legend_svg?: string) => void;
  isSegmented: boolean;
  isDate?: boolean;
  isShareView?: boolean;
  xDateItemsToRender?: number;
  chartLoading?: boolean;
  renderAsSvg?: boolean;
  needDequeuedChart?: boolean;
  isSelfChart: boolean;
  isNumber: boolean;
  title?: string;
  chartOperation?: string;
  defaultDateGroup?: string;
  isYPercentage: boolean;
  isXPercentage: boolean;
  showWaterMark?: boolean;
  setReloadDataTable?: React.Dispatch<React.SetStateAction<boolean>>;
  onClickRemoveWaterMark?: () => void;
  skipEmptyPeriods: boolean;
  isBoardPreview?: boolean;
  xAxisField: string;
}

const getChartOperation = (operation: string) => {
  switch (operation) {
    case 'COUNT':
      return 'Count';
    case 'UNIQUECOUNT':
      return 'Unique Count';
    case 'SUM':
      return 'Sum';
    case 'AVG':
      return 'Average';
    case 'MAX':
      return 'Maximum';
    case 'MIN':
      return 'Minimum';
    case 'MEDIAN':
      return 'Median';
    default:
      return operation;
  }
};

const Chart: FC<Props> = ({
  chartViewRef,
  chartId,
  data,
  chartConfig,
  chartType,
  width = '100%',
  height = '100%',
  visualSettings,
  handleOnChartReady = () => null,
  hideAxisLabels,
  isPreview,
  isShareView,
  isProjectBoard,
  colorSet,
  dateGroup,
  getSvg,
  isSegmented,
  isDate,
  xDateItemsToRender = 60,
  chartLoading,
  renderAsSvg,
  needDequeuedChart = false,
  isSelfChart = false,
  isNumber = false,
  title,
  chartOperation: operationType,
  defaultDateGroup,
  isYPercentage,
  isXPercentage,
  showWaterMark = false,
  setReloadDataTable,
  onClickRemoveWaterMark,
  skipEmptyPeriods = false,
  isBoardPreview = false,
  xAxisField
}) => {
  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );
  const currentChartPreviewData = useSelector(
    (state: RootState) => state?.currentChartPreviewData
  );

  const [isLoading, setIsLoading] = useState(false);
  const chartRef = useRef<any>(null);
  const [mobileView, setMobileView] = useState<boolean>(false);
  const [renderedSvg, setRenderedSvg] = useState('');
  const [legendContainer, setLegendContainer] = useState<am4core.Container>();

  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const activeDateGroup = useMemo(() => {
    if (dateGroup === 'none') return defaultDateGroup || DATE_GROUP.DAYS;
    else return dateGroup;
  }, [dateGroup]);
  // const [chartLoading, setDebouncedChartLoading] = useState(false);

  const xAxisType = currentChartData.config?.x_axis?.treat_as_type;

  useEffect(() => {
    if (!chartLoading) setIsLoading(true);
  }, [projectBoard?.currentProjectData?.filters]);

  // useEffect(() => {
  //   if (chartLoading && !chartLoading) setDebouncedChartLoading(true);
  //   else if (chartLoading === false && chartLoading)
  //     setTimeout(() => setDebouncedChartLoading(false), 1000);
  // }, [chartLoading]);
  useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) setMobileView(true);
      else setMobileView(false);
    }

    /** The event listener that's calling the displayMobileNavbar function when resizing the window.
     */
    window.addEventListener('resize', displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', displayMobileNavbar);
  }, []);

  const chartRefId = `${chartId}${isPreview ? '-preview' : ''}${
    isProjectBoard ? '-board' : ''
  }`;

  const projectVisualSettings =
    projectBoard.currentProjectData?.visual_settings;

  const chartStoreData = useMemo(
    () => (isShareView ? currentChartPreviewData : currentChartData),
    [isShareView, currentChartPreviewData, currentChartData]
  );

  const chartOperation = isShareView
    ? currentChartPreviewData.config.chart_operation
    : isPreview || isProjectBoard
    ? operationType
    : currentChartData.config.chart_operation;

  const filterDisabledOperations = ['MIN', 'MAX', 'MEDIAN', 'AVG'];

  const {
    sortOrder,
    filters,
    filtersToggle,
    showDataLabels,
    stackChart,
    showLegends,
    gradient_charts,
    multicolor_charts,
    autoGroupNumbers,
    kpiTarget,
    size,
    theme_connection_status,
    showAsPercentage
  } = visualSettings || {};

  const sortedData = useMemo(() => {
    if (sortOrder && sortOrder !== 'default' && !isDate) {
      const sorted = [...(data || [])];
      let splitSorted = [];
      //Axis with number value
      let numberAxis = '';

      const dataKeys = Object.keys(sorted?.[0] || {});

      if (dataKeys.length === 2)
        dataKeys.forEach((key) => {
          if (typeof sorted[0][key] === 'number') numberAxis = key;
        });
      else
        splitSorted = sorted.map((item) => {
          const values = Object.values(item);

          const stringIndex = xAxisField
            ? Object.keys(item).indexOf(xAxisField)
            : 0;

          values.splice(stringIndex, 1);
          const totalValue = values.reduce(
            (accumulator: any, currentValue: any) => accumulator + currentValue,
            0
          );

          return { ...item, totalValue };
        });

      (splitSorted.length ? splitSorted : sorted).sort((a: any, b: any) => {
        let first = a[dataKeys.length === 2 ? numberAxis : 'totalValue'];
        let second = b[dataKeys.length === 2 ? numberAxis : 'totalValue'];

        const isBarChart = chartType === 'Bar';

        if (
          (sortOrder === 'ascending' && !isBarChart) ||
          (sortOrder === 'descending' && isBarChart)
        ) {
          first = b[dataKeys.length === 2 ? numberAxis : 'totalValue'];
          second = a[dataKeys.length === 2 ? numberAxis : 'totalValue'];
        }
        if (first > second) return -1;

        if (second > first) return 1;

        return 0;
      });

      return splitSorted.length ? splitSorted : sorted;
    } else {
      return data;
    }
  }, [sortOrder, data, chartType]);

  const xAxisLabelsExcluded = useMemo(() => {
    if (filters?.type === '') return [];

    const sorted = [...(data || [])];
    let splitSorted = [];
    //Axis with number value
    let numberAxis = '';
    let xAxisKey = '';

    const dataKeys = Object.keys(sorted?.[0] || {});

    if (dataKeys.length === 2)
      dataKeys.forEach((key) => {
        if (typeof sorted[0][key] === 'number') numberAxis = key;
        else xAxisKey = key;
      });
    else
      splitSorted = sorted.map((item) => {
        const values = Object.values(item);
        const stringIndex = values.findIndex((val) => typeof val === 'string');

        xAxisKey = dataKeys[stringIndex];

        values.splice(stringIndex, 1);
        const totalValue = values.reduce(
          (accumulator: any, currentValue: any) => accumulator + currentValue,
          0
        );

        return { ...item, totalValue };
      });

    (splitSorted.length ? splitSorted : sorted).sort((a: any, b: any) => {
      let first = a[dataKeys.length === 2 ? numberAxis : 'totalValue'];
      let second = b[dataKeys.length === 2 ? numberAxis : 'totalValue'];

      if (filters?.type === 'bottom') {
        first = b[dataKeys.length === 2 ? numberAxis : 'totalValue'];
        second = a[dataKeys.length === 2 ? numberAxis : 'totalValue'];
      }
      if (first > second) return -1;

      if (second > first) return 1;

      return 0;
    });

    (splitSorted.length ? splitSorted : sorted).splice(
      filters?.range?.startInd as number,
      filters?.range?.endInd as number
    );

    const newExcludedLabels = (splitSorted.length ? splitSorted : sorted).map(
      (obj: any) => obj[xAxisKey]
    );

    return newExcludedLabels;
  }, [data, filters]);

  const xAxisKey =
    chartType === 'PIE'
      ? chartConfig.series?.[0].dataFields.category
      : chartType === 'Bar'
      ? chartConfig.xAxes?.[0].dataFields.value
      : chartConfig.xAxes?.[0].dataFields.category;

  const yAxisKey =
    chartType === 'Bar'
      ? chartConfig.yAxes?.[0].dataFields.category
      : chartConfig.yAxes?.[0].dataFields.value;

  const filteredData = useMemo(() => {
    if (
      (filtersToggle || filters?.type !== 'none') &&
      (chartType === 'KPI'
        ? !filterDisabledOperations.includes(chartOperation)
        : true)
    ) {
      const xAxis =
        chartType === 'PIE'
          ? chartConfig.series?.[0].dataFields.category
          : chartType === 'Bar'
          ? chartConfig.yAxes?.[0].dataFields.category
          : chartConfig.xAxes?.[0].dataFields.category;

      if (sortedData && filters)
        if (filters.type === 'top' || filters?.type === 'bottom')
          return sortedData.filter(
            (item: any) => !xAxisLabelsExcluded.includes(item[xAxis])
          );

      let nonZeroChartData = sortedData;

      if (chartType === 'PIE') {
        const yAxisKey = chartConfig.series?.[0].dataFields.value;

        nonZeroChartData = sortedData?.filter((item: any) => item[yAxisKey]);
      }

      return nonZeroChartData;
    } else {
      return sortedData;
    }
  }, [
    sortedData,
    chartConfig,
    filters?.xAxisLabelsExcluded,
    filtersToggle,
    xAxisLabelsExcluded
  ]);

  const totalDataPoints = useMemo(() => {
    const sumOfNumbers = filteredData.reduce((accumulator: any, item: any) => {
      const secondProperty = Object.values(item)[1];

      if (typeof secondProperty === 'number')
        return accumulator + secondProperty;
      else return accumulator; // Skip if the second property is not a number
    }, 0);

    const roundedTotalDataPoints = roundDecimal(sumOfNumbers);

    return roundedTotalDataPoints;
  }, [filteredData]);

  const meterValues = useMemo(() => {
    if (typeof kpiTarget !== 'undefined' && kpiTarget !== null) {
      const minValue = Math.min(totalDataPoints, kpiTarget);
      const maxValue = Math.max(totalDataPoints, kpiTarget);

      let startValue = 0;
      let endValue = 0;

      if (kpiTarget > 0 && totalDataPoints > 0) {
        endValue = 1.1 * maxValue;
      } else if (kpiTarget < 0 && totalDataPoints < 0) {
        startValue = minValue - Math.abs(minValue) * 0.1;
        endValue = 0;
      } else {
        startValue =
          minValue - Math.abs(minValue) * 0.1 === 0
            ? -1
            : minValue - Math.abs(minValue) * 0.1;
        endValue =
          maxValue + Math.abs(maxValue) * 0.1 === 0
            ? 1
            : maxValue + Math.abs(maxValue) * 0.1;
      }

      return {
        startValue,
        endValue
      };
    }
  }, [totalDataPoints, kpiTarget]);

  const modifiedConfig = useMemo(() => {
    const newConfig = {
      ...chartConfig,
      id: chartRefId
    };

    let kpiChartConfig = {};

    if (typeof kpiTarget !== 'undefined' && kpiTarget !== null)
      kpiChartConfig = {
        type: 'GaugeChart',
        innerRadius: '80%',
        xAxes: [
          {
            type: 'ValueAxis',
            min: meterValues?.startValue,
            max: meterValues?.endValue,
            strictMinMax: true,
            renderer: {
              minGridDistance: 1,
              grid: {
                strokeOpacity: 0
              },
              labels: {
                template: {
                  disabled: true
                }
              }
            }
          }
        ],
        hands: [
          {
            type: 'ClockHand',
            value: totalDataPoints
          }
        ],
        colors: {},
        id: chartRefId,
        metaInfo: {
          chart_id: chartRefId
        }
      };

    newConfig.metaInfo.chart_id = chartRefId;

    if (newConfig && chartType !== 'KPI') {
      const dateGroupString = activeDateGroup || DATE_TIME_GROUP.DAYS;

      const xAxis =
        chartType === 'PIE'
          ? chartConfig.series?.[0].dataFields.category
          : chartType === 'Bar'
          ? chartConfig.yAxes?.[0].dataFields.category
          : chartConfig.xAxes?.[0].dataFields.category;

      const startDate = dayjs(filteredData?.[0]?.[xAxis]);
      const endDate = dayjs(filteredData?.[filteredData.length - 1]?.[xAxis]);
      const dateDiff = isDate
        ? endDate.diff(startDate, dateGroupString as any)
        : 0;
      const xAxisItems =
        chartStoreData.config?.date_group === 'none'
          ? filteredData.length
          : dateDiff;

      const baseInterval =
        xAxisItems > xDateItemsToRender
          ? Math.ceil(dateDiff / xDateItemsToRender)
          : 1;

      if (chartType !== 'PIE' && newConfig?.xAxes?.[0] && isDate)
        newConfig.xAxes[0] = {
          ...newConfig.xAxes[0],
          baseInterval: getBaseInterval(dateGroupString as DATE_GROUP | DATE_TIME_GROUP, 1),
          dateFormats: {
            // Check if dateGroupString is part of DATE_GROUP_WITHOUT_FORMAT
            ...(Object.values(DATE_GROUP_WITHOUT_FORMAT).includes(dateGroupString as DATE_GROUP | DATE_TIME_GROUP) ? {} : {
              second: 'ss',
              minute: 'mm',
              hour: 'HH:mm',
              day: 'dd-MM-yyyy',
              week: 'ww-yyyy',
              ...getYearMonthFormat(dateGroupString as DATE_GROUP | DATE_TIME_GROUP)
            })
          },
          periodChangeDateFormats: {
            // Check if dateGroupString is part of DATE_GROUP_WITHOUT_FORMAT
            ...(Object.values(DATE_GROUP_WITHOUT_FORMAT).includes(dateGroupString as DATE_GROUP | DATE_TIME_GROUP) ? {} : {
              second: 'mm:ss',
              minute: 'HH:mm',
              hour: 'dd-MM-yy HH:mm',
              day: 'dd-MM-yyyy',
              week: 'ww-yyyy',
              ...getYearMonthFormat(dateGroupString as DATE_GROUP | DATE_TIME_GROUP)
            })
          },
          // skipEmptyPeriods: isProjectBoard
          //   ? dateGroup === 'none'
          //     ? true
          //     : false
          //   : chartStoreData.config?.date_group === 'none'
          //   ? true
          //   : false,
          skipEmptyPeriods: skipEmptyPeriods,
          cursorTooltipEnabled: true,
          groupData: false,
          gridIntervals: [getBaseInterval(dateGroupString as DATE_GROUP | DATE_TIME_GROUP, baseInterval)]
        };

      if (colorSet) {
        const processedColors = colorSet?.map((col: string) =>
          col.toUpperCase()
        );

        if (chartType === 'PIE')
          newConfig.series[0].colors = { list: processedColors };
        else newConfig.colors = { list: processedColors };
      } else {
        if (chartType === 'PIE')
          newConfig.series[0].colors = { list: [...THEME_STYLES.DEFAULT] };
      }

      if (
        newConfig?.yAxes?.[0]?.title?.text &&
        newConfig?.xAxes?.[0]?.title?.text
      ) {
        const formattedYaxisTitle = formatChartAxisTitle(
          newConfig?.yAxes?.[0]?.title?.text,
          isProjectBoard ? 25 : 45
        );

        if (chartType !== 'Bar') {
          newConfig.yAxes[0].title.text = isSelfChart
            ? 'Count of rows'
            : formattedYaxisTitle;
        } else {
          newConfig.xAxes[0].title.text = isSelfChart
            ? 'Count of rows'
            : newConfig?.xAxes?.[0]?.title?.text;
          newConfig.yAxes[0].title.text = formattedYaxisTitle;
        }

        if (chartType === 'Bar') {
          newConfig.xAxes[0].cursorTooltipEnabled = false;
          newConfig.yAxes[0].cursorTooltipEnabled = isProjectBoard
            ? false
            : true;
        } else {
          newConfig.xAxes[0].cursorTooltipEnabled = isProjectBoard
            ? false
            : true;
          newConfig.yAxes[0].cursorTooltipEnabled = false;
        }
      }

      //Handle autoGrouping for segmented charts
      if ((chartType === 'Column' || chartType === 'Bar') && isSegmented)
        newConfig.series = newConfig.series.map((series: any) => ({
          ...series,
          columns: {
            ...series?.columns,
            width: autoGroupNumbers && isNumber ? '100%' : '80%',
            height:
              autoGroupNumbers && isNumber
                ? '100%'
                : chartType === 'Bar'
                ? '80%'
                : '100%',
            stroke:
              autoGroupNumbers && isNumber
                ? am4core.color('#000000')
                : am4core.color('#FFFFFF'),
            strokeOpacity: 0.2
          }
        }));

      // Append X-axis to tooltip for segmented charts in PB
      if (isSegmented && isProjectBoard) {
        if (chartType === 'Column' || chartType === 'Bar')
          newConfig.series = newConfig.series.map((series: any) => ({
            ...series,
            columns: {
              ...series?.columns,
              tooltipText: (series.columns.tooltipText +=
                chartType === 'Column'
                  ? isDate
                    ? ` (${getDateFormatForTooltip(activeDateGroup as any)})`
                    : " ({categoryX.formatNumber('#,###.##')})"
                  : " ({categoryY.formatNumber('#,###.##')})")
            }
          }));

        if (chartType === 'Line')
          newConfig.series = newConfig.series.map((series: any) => ({
            ...series,
            tooltipText: (series.tooltipText += isDate
              ? ` (${getDateFormatForTooltip(activeDateGroup as any)})`
              : " ({categoryX.formatNumber('#,###.##')})")
          }));
      }

      if (chartType !== 'Bar' && newConfig?.xAxes?.[0].tooltipText)
        newConfig.xAxes[0].tooltipText = '';

      if (chartType === 'Bar' && newConfig?.yAxes?.[0].tooltipText)
        newConfig.yAxes[0].tooltipText = '';

      newConfig.legend = {
        ...newConfig.legend,
        position: chartType === 'Bar' ? 'top' : 'bottom',
        maxHeight: 65,
        labels: {
          template: {
            fontSize: '0.75rem'
          }
        },
        markers: {
          template: {
            width: 10,
            height: 10
          }
        },
        disabled: isProjectBoard || !isSegmented ? true : !showLegends,
        scrollbar: { background: { fill: 'black' } },
        itemContainers: {
          template: {
            marginTop: '10px',
            marginBottom: '0px',
            paddingTop: 0,
            paddingBottom: '5px'
          }
        }
      };

      //To show maximum of 75 characters in the Y Axis of bar chart
      if (chartType === 'Bar' && newConfig?.yAxes?.[0]?.renderer) {
        const yAxis = newConfig.yAxes[0];
        const yRenderer = yAxis.renderer;

        yRenderer.labels.template.maxWidth = 1000;
      }
      if (chartType !== 'Bar' && newConfig?.xAxes?.[0]?.renderer) {
        const xAxis = newConfig.xAxes[0];
        const xRenderer = xAxis.renderer;

        xRenderer.labels.template.maxWidth = 300;
      }

      if (chartType === 'Bar' && getSvg && newConfig?.xAxes?.[0]?.renderer) {
        const xAxis = newConfig.xAxes[0];
        const yAxis = newConfig.yAxes[0];
        const xRenderer = xAxis.renderer;
        const yRenderer = yAxis.renderer;

        xRenderer.minGridDistance = 70;
        yRenderer.minGridDistance = 12.3;
      }

      //To show dark color labels in report
      if (
        chartType !== 'PIE' &&
        newConfig?.xAxes?.[0] &&
        newConfig?.yAxes?.[0] &&
        getSvg
      ) {
        const xAxis = newConfig.xAxes[0];
        const yAxis = newConfig.yAxes[0];
        const xRenderer = xAxis.renderer;
        const yRenderer = yAxis.renderer;

        yAxis.title.fill = colors.dark.main;
        xAxis.title.fill = colors.dark.main;

        xRenderer.labels.template.fill = colors.dark.main;
        yRenderer.labels.template.fill = colors.dark.main;
      }
      if (chartType === 'PIE' && newConfig?.series?.[0] && getSvg)
        newConfig.series[0].labels.fill = colors.dark.main;

      if (hideAxisLabels) {
        if (chartType !== 'PIE') {
          const xAxis = newConfig.xAxes[0];
          const yAxis = newConfig.yAxes[0];
          const xRenderer = xAxis.renderer;
          const yRenderer = yAxis.renderer;

          xRenderer.labels.template.disabled = true;
          xRenderer.grid.disabled = true;
          xRenderer.minWidth = 0;

          yRenderer.labels.template.disabled = true;
          if (yRenderer?.grid) yRenderer.grid.disabled = true;
          yRenderer.minWidth = 0;

          yAxis.title.fontSize = 16;
          xAxis.title.fontSize = 16;
        }
      } else if (isShareView && chartType !== 'PIE') {
        const yRenderer = newConfig.yAxes?.[0]?.renderer;
        const xRenderer = newConfig.xAxes?.[0]?.renderer;

        if (mobileView && xRenderer.labels.template) {
          xRenderer.labels.template.rotation = -65;
          xRenderer.labels.template.maxWidth = 100;
        }

        if (mobileView && yRenderer.labels.template)
          yRenderer.labels.template.disabled = true;
        else yRenderer.labels.template.disabled = false;
      }
    }

    return chartType === 'KPI' ? kpiChartConfig : newConfig;
  }, [
    chartConfig,
    hideAxisLabels,
    colorSet,
    activeDateGroup,
    showLegends,
    mobileView,
    visualSettings?.['3d_charts'],
    chartStoreData.config?.date_group,
    //Chart loaded twice due to this dependency
    visualSettings?.kpiTarget,
    autoGroupNumbers,
    isNumber,
    totalDataPoints
  ]);

  const activeColorSet = useMemo(
    () =>
      chartType === 'PIE'
        ? [...modifiedConfig.series[0].colors.list]
        : [...(modifiedConfig?.colors?.list ?? [])],
    [colorSet]
  );

  useEffect(() => {
    if (chartType === 'PIE') {
      const finalColorSet = modifiedConfig.series[0].colors.list;

      if (sortOrder === 'ascending') {
        const numberAxis =
          Object.keys(filteredData?.[0] || {}).find(
            (key) => typeof filteredData[0][key] === 'number'
          ) || '';

        const sumOfAllData = filteredData.reduce(
          (sum: number, a: any) => sum + a[numberAxis],
          0
        );

        const othersCount = filteredData.findIndex(
          (item: any) => (item[numberAxis] / sumOfAllData) * 100 > 5
        );

        if (othersCount > 0) {
          const movedColors = finalColorSet.splice(
            0,
            filteredData.length - (othersCount - 1)
          );

          finalColorSet.splice(othersCount);
          finalColorSet.push(...movedColors);
        }
      } else {
        finalColorSet.splice(0, finalColorSet.length, ...activeColorSet);
      }
    }
  }, [sortOrder, visualSettings?.['3d_charts'], filteredData, colorSet]);

  const toggleDataLabels = () => {
    if (chartRef.current && chartType !== 'KPI') {
      const isDefaultPie =
        chartType === 'PIE' && visualSettings?.showDataLabels === undefined;

      if ((visualSettings?.showDataLabels || isDefaultPie) && chartRef.current)
        chartRef.current?.series.values.forEach((series: any) => {
          if (series?.bullets) {
            const labelBullet = series?.bullets?.push(new LabelBullet());

            if (chartType === 'Bar') {
              labelBullet.dx = 10;
              labelBullet.label.text = '';
            } else {
              labelBullet.dy = -10;
              labelBullet.label.text = '';
            }
          }

          if (chartType === 'PIE' && series.ticks) {
            series.ticks.template.disabled = false;
            series.labels.template.radius = 10;
            series.labels.template.disabled = false;
            series.labels.template.text = isProjectBoard
              ? '{category}'
              : series.labels.template.text;
            series.labels.template.wrap = isProjectBoard ? false : true;
            series.labels.template.truncate = isProjectBoard ? true : false;
            series.labels.template.maxWidth = isProjectBoard ? 150 : 250;
          }
        });
      else if (chartRef.current)
        chartRef.current?.series.values.forEach((series: any) => {
          if (series?.bullets) series?.bullets?.pop();

          if (chartType === 'PIE') {
            series.ticks.template.disabled = true;
            series.labels.template.radius = 20;
            series.labels.template.disabled = true;
            series.labels.template.text = '';

            setTimeout(() => {
              series.bulletsContainer.visible = false;
            }, 100);
          }
        });
    }
  };

  if (chartRef.current && isProjectBoard) {
    if (chartType === 'Bar') {
      const axisLine = chartRef.current.xAxes?.values?.[0];

      if (axisLine?.renderer) axisLine.renderer.baseGrid.visible = false;
    }

    if (chartType === 'Column' || chartType === 'Line') {
      const axisLine = chartRef.current.yAxes?.values?.[0];

      if (axisLine?.renderer) axisLine.renderer.baseGrid.visible = false;
    }
  }

  const toggleGradient = () => {
    //Hook to handle gradient for Line chart,Segmented Column or Bar Chart and Pie Chart
    if (chartRef.current && chartType !== 'KPI')
      if (chartType === 'Line' || isSegmented)
        chartRef.current?.series?.values.forEach((series: any) => {
          if (series?.fill?.stops)
            series.fill = series.fill.stops.values[0].color;

          if (
            !theme_connection_status?.gradient_charts
              ? gradient_charts
              : projectVisualSettings?.gradient_charts
          ) {
            const gradient = new am4core.LinearGradient();

            series.tooltip.pointerOrientation = 'vertical';
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = series.fill;
            series.fillOpacity = chartType === 'Line' ? 0.5 : 1;

            gradient.rotation = -90;

            gradient.addColor(series.fill, 0.5);
            gradient.addColor(series.fill, 1);

            series.fill = gradient;
            if (chartType === 'Line') {
              series.smoothing = 'monotoneX';
              series.strokeWidth = 2;
            }
          }
        });
      else if (chartType === 'PIE')
        chartRef.current?.series?.values?.[0]?.slices?.values?.forEach(
          (slice: any) => {
            if (slice.fill.stops) slice.fill = slice.fill.stops.values[0].color;

            if (
              !theme_connection_status?.gradient_charts
                ? gradient_charts
                : projectVisualSettings?.gradient_charts
            ) {
              const gradient = new am4core.LinearGradient();

              slice.stroke = am4core.color('#FFFF');

              gradient.stops.push({
                color: am4core.color(slice.fill.hex),
                offset: 0
              });
              gradient.stops.push({
                color: am4core.color(slice.fill.hex, 0.5),
                offset: 1
              });
              slice.fill = gradient;
            }
          }
        );
  };

  const use3DCharts = !theme_connection_status?.['3d_charts']
    ? visualSettings?.['3d_charts']
    : projectVisualSettings?.['3d_charts'];

  const toggleStackChart = () => {
    if (visualSettings?.stackChart && chartRef.current)
      chartRef.current?.series.values.forEach((series: any) => {
        series.stacked = true;
      });
    else
      chartRef.current?.series.values.forEach((series: any) => {
        series.stacked = false;
      });

    const categoryAxis = chartRef?.current?.xAxes?.values?.[0];

    if (categoryAxis?.renderer)
      if (
        visualSettings?.stackChart &&
        chartRef.current &&
        chartType !== 'PIE'
      ) {
        categoryAxis.renderer.cellStartLocation = 0;
        categoryAxis.renderer.cellEndLocation = 1;
      } else if (isSegmented && chartRef.current) {
        categoryAxis.renderer.cellStartLocation = 0.1;
        categoryAxis.renderer.cellEndLocation = 0.9;
      }
  };

  const createDeferredChart = (
    configToRender: any,
    onChartCreationCallback: (chart: any) => void
  ) => {
    const configAndData =
      chartType === 'KPI'
        ? configToRender
        : {
            ...configToRender,
            data: filteredData
          };

    am4core
      .createDeferred(() => {
        chartRef.current = createFromConfig(
          configAndData,
          chartRefId,
          chartType === 'KPI'
            ? GaugeChart
            : use3DCharts && chartType !== 'Line'
            ? chartType === 'PIE'
              ? PieChart3D
              : XYChart3D
            : chartType === 'PIE'
            ? PieChart
            : XYChart
        );

        return chartRef.current;
      }, chartRefId)
      .then(async (chart: any) => onChartCreationCallback(chart));
  };

  //To avoid repeating Y axis values and to always start axis values from 0
  const yAxis = chartRef?.current?.yAxes?.getIndex(0);
  const xAxis = chartRef?.current?.xAxes?.getIndex(0);
  const maxYVal = yAxis?.max;

  useEffect(() => {
    // if (chartType === 'Bar' && xAxis && !isSegmented)
    //   if (maxXVal <= 2 && !isXPercentage) {
    //     xAxis.max = 2;
    //     xAxis.min = Math.min(0, xAxis.min);
    //   }
    if (yAxis && !isSegmented)
      if (maxYVal <= 2 && !isYPercentage) {
        yAxis.max = 2;
        yAxis.min = Math.min(0, yAxis.min);
      }
  }, [maxYVal, yAxis, xAxis, chartType]);

  const createDequeueChart = (
    configToRender: any,
    onChartCreationCallback: (chart: any) => void
  ) => {
    const configAndData =
      chartType === 'KPI'
        ? configToRender
        : { ...configToRender, data: filteredData };

    chartRef.current = createFromConfig(
      configAndData,
      chartRefId,
      chartType === 'KPI'
        ? GaugeChart
        : use3DCharts && chartType !== 'Line'
        ? chartType === 'PIE'
          ? PieChart3D
          : XYChart3D
        : chartType === 'PIE'
        ? PieChart
        : XYChart
    );
    onChartCreationCallback(chartRef.current);
  };

  const renderChart = useDebouncedCallback(() => {
    const configToRender = {
      ...modifiedConfig
    };

    const charLimit = chartType === 'Bar' ? 75 : 20;

    if (configToRender && chartType !== 'KPI')
      ['xAxes', 'yAxes'].forEach((axis: string, index: number) => {
        const isxAxis = index === 0;
        const numberFormatter = createNumberFormatter(
          chartType === 'Bar' ? !isxAxis : isxAxis
        );

        if (configToRender[axis]?.[0])
          configToRender[axis][0].numberFormatter = numberFormatter;

        if (autoGroupNumbers && isNumber && configToRender[axis]?.[0])
          configToRender[axis][0].renderer.labels.template.adapter = [
            {
              key: 'text',
              callback: function (label: any, target: any) {
                if (target?.dataItem?.category && label) {
                  const rangeRegex =
                    /^(-?[\d,]+(?:\.\d+)?)\s*-\s*(-?[\d,]+(?:\.\d+)?)$/;
                  const match = target?.dataItem?.category?.match(rangeRegex);

                  if (match) {
                    const min = parseFloat(match[1].replace(/,/g, ''));
                    const max = parseFloat(match[2].replace(/,/g, ''));

                    return `${numberFormatter?.format(
                      min
                    )}-${numberFormatter?.format(max)}`;
                  } else {
                    return target?.dataItem?.category;
                  }
                }

                return label;
              }
            }
          ];
        else if (configToRender[axis]?.[0])
          configToRender[axis][0].renderer.labels.template.adapter =
            typeof filteredData?.[0]?.[index === 0 ? xAxisKey : yAxisKey] ===
              'string' && !isDate
              ? [
                  {
                    key: 'text',
                    callback: function (label: any, target: any) {
                      if (
                        target?.dataItem?.category &&
                        label &&
                        typeof target?.dataItem?.category === 'string'
                      ) {
                        if (target?.dataItem?.category?.length <= charLimit)
                          return target?.dataItem?.category;

                        return (
                          target?.dataItem?.category?.slice(0, charLimit) +
                          '...'
                        );
                      }

                      return label;
                    }
                  }
                ]
              : typeof filteredData?.[0]?.[
                  index === 0 ? xAxisKey : yAxisKey
                ] === 'number' &&
                !isDate &&
                (index === 0 ? !isXPercentage : !isYPercentage)
              ? [
                  {
                    key: 'text',
                    callback: function (_label: any, target: any) {
                      const axisName =
                        (index === 0 && chartType === 'Bar') ||
                        (index !== 0 && chartType !== 'Bar')
                          ? 'value'
                          : 'category';

                      if (typeof target?.dataItem?.[axisName] === 'string')
                        return target?.dataItem?.[axisName];

                      if (target?.dataItem?.[axisName] >= 10 ** 31) {
                        const scientificNotation = Number(
                          target?.dataItem?.[axisName]
                        ).toExponential(0);

                        return scientificNotation;
                      } else {
                        return numberFormatter.format(
                          target?.dataItem?.[axisName]
                        );
                      }
                    }
                  }
                ]
              : [];
      });

    if (chartRef.current && !isProjectBoard) chartRef.current?.dispose();

    if (
      chartType === 'KPI' &&
      typeof kpiTarget !== 'undefined' &&
      kpiTarget !== null
    ) {
      const firstValue =
        totalDataPoints < kpiTarget ? totalDataPoints : kpiTarget;

      const secondValue =
        totalDataPoints < kpiTarget ? kpiTarget : totalDataPoints;

      configToRender.xAxes[0] = {
        ...configToRender.xAxes?.[0],
        axisRanges: [
          {
            value: meterValues?.startValue,
            endValue: firstValue,
            axisFill: {
              fillOpacity: 1,
              fill: (() => {
                const processedColors =
                  colorSet && colorSet.length > 0
                    ? colorSet.map((col: string) =>
                        am4core.color(col.toUpperCase())
                      )
                    : THEME_STYLES.DEFAULT.map((color) => am4core.color(color));

                if (
                  !theme_connection_status?.gradient_charts
                    ? gradient_charts
                    : projectVisualSettings?.gradient_charts
                ) {
                  const gradient = new am4core.LinearGradient();

                  gradient.addColor(processedColors[0], 0.5);
                  gradient.addColor(processedColors[0], 1);

                  gradient.rotation = -90;

                  return gradient;
                } else {
                  return processedColors[0];
                }
              })()
            }
          },

          {
            value: firstValue,
            endValue: secondValue,
            grid: {
              stroke: am4core.color('black'),
              strokeOpacity: 1
            },
            label: {
              ...(getSvg && { fill: colors.dark.main }),
              text:
                firstValue === totalDataPoints
                  ? `${roundToNearestMagnitude(totalDataPoints)}`
                  : `Target: ${roundToNearestMagnitude(kpiTarget)}`
            },
            axisFill: {
              fillOpacity: 1,
              fill: (() => {
                const processedColors =
                  colorSet && colorSet.length > 0
                    ? colorSet.map((col: string) =>
                        am4core.color(col.toUpperCase())
                      )
                    : THEME_STYLES.DEFAULT.map((color) => am4core.color(color));

                const color =
                  secondValue === kpiTarget
                    ? processedColors[0]
                    : processedColors[1];

                const randomColor = new am4core.ColorSet().next();

                if (
                  !theme_connection_status?.gradient_charts
                    ? gradient_charts
                    : projectVisualSettings?.gradient_charts
                ) {
                  const gradient = new am4core.LinearGradient();

                  gradient.addColor(color || randomColor, 0.5);
                  gradient.addColor(color || randomColor, 1);

                  return gradient;
                } else {
                  return color || randomColor;
                }
              })()
            }
          },

          {
            value: secondValue,
            grid: {
              stroke: am4core.color('black'),
              strokeOpacity: 1
            },
            endValue: meterValues?.endValue,
            label: {
              ...(getSvg && { fill: colors.dark.main }),
              text:
                secondValue === totalDataPoints
                  ? `${
                      totalDataPoints === kpiTarget ? 'Target:' : ''
                    } ${roundToNearestMagnitude(totalDataPoints)}`
                  : `Target: ${roundToNearestMagnitude(kpiTarget)}`,
              location: meterValues?.endValue
                ? Math.abs(kpiTarget - totalDataPoints) /
                    meterValues?.endValue <
                  5 / 180
                  ? 0.5
                  : 0
                : 0
            },
            axisFill: {
              fillOpacity: 1,
              fill: (() => {
                const processedColors =
                  colorSet && colorSet.length > 0
                    ? colorSet.map((col: string) =>
                        am4core.color(col.toUpperCase())
                      )
                    : THEME_STYLES.DEFAULT.map((color) => am4core.color(color));

                const randomColor = new am4core.ColorSet().next();

                return processedColors[1] || randomColor;
              })()
            }
          }
        ]
      };
    }

    if (chartType !== 'KPI') {
      if (isPreview && isSegmented) {
        const container = am4core.create(
          `legend-div-${chartRefId}`,
          am4core.Container
        );

        container.width = am4core.percent(100);
        container.height = am4core.percent(100);
        configToRender.legend.parent = container;
        setLegendContainer(container);
      }

      const dateGroupString = getDayJsUnitString(
        activeDateGroup || DATE_GROUP.DAYS
      );

      const xRenderer = configToRender?.xAxes?.[0]?.renderer;

      if (xRenderer && chartType !== 'Bar' && chartType !== 'KPI')
        if (isNotTimeSeries && chartType !== 'PIE')
          xRenderer.minGridDistance = 50;
        else
          xRenderer.minGridDistance =
            chartConfig?.xAxes?.[0].renderer.minGridDistance;

      if (
        isDate &&
        filteredData.length > 1 &&
        dateGroupString &&
        chartType !== 'PIE' &&
        chartType !== 'KPI'
      ) {
        const xAxis = configToRender.xAxes?.[0].dataFields.category;
        const startDate = dayjs(filteredData?.[0]?.[xAxis]);
        const endDate = dayjs(filteredData?.[filteredData.length - 1]?.[xAxis]);
        const dateDiff = isDate
          ? skipEmptyPeriods
            ? filteredData?.length
            : endDate.diff(startDate, dateGroupString)
          : 0;

        const baseInterval = Math.ceil(dateDiff / xDateItemsToRender);

        const xAxisItems = dateDiff;

        const { gridIntervals, ...newConfig } = configToRender.xAxes[0];

        if (skipEmptyPeriods) {
          configToRender.xAxes[0] = newConfig;
          if (xAxisItems > xDateItemsToRender)
            configToRender.xAxes[0].gridIntervals = [
              getBaseInterval(activeDateGroup || DATE_GROUP.DAYS, baseInterval)
            ];
          else
            configToRender.xAxes[0].gridIntervals = [
              getBaseInterval(activeDateGroup || DATE_GROUP.DAYS, 1)
            ];
        } else if (xAxisItems > xDateItemsToRender) {
          configToRender.xAxes[0].gridIntervals = [
            getBaseInterval(activeDateGroup || DATE_GROUP.DAYS, baseInterval)
          ];
        } else {
          configToRender.xAxes[0].gridIntervals = [
            getBaseInterval(activeDateGroup || DATE_GROUP.DAYS, 1)
          ];
        }
      }
      if (chartType === 'Column')
        if (filteredData.length > 1500 && !isDate)
          configToRender.series = configToRender.series.map((series: any) => ({
            ...series,
            type: 'StepLineSeries',
            fillOpacity: 1,
            smoothing: 'monotoneX',
            strokeWidth: 2
          }));
        else configToRender.series = [...modifiedConfig.series];
      if (isDate)
        if (chartType === 'PIE') {
          configToRender.series[0].labels.text = showAsPercentage
            ? `${getDateFormatForTooltip(
                activeDateGroup as any,
                true
              )}: {value.formatNumber('#,###.##')}%`
            : `${getDateFormatForTooltip(
                activeDateGroup as any,
                true
              )}: {value.formatNumber('#,###.##')} ({value.percent.formatNumber('#.#|#.#s')}%)`;
          configToRender.series[0].labels.adapter = {
            text: function (text: any, target: any) {
              return target.dataItem.category == 'Other'
                ? showAsPercentage
                  ? "{category} : {value.formatNumber('#,###.##')}%"
                  : "{category} : {value.formatNumber('#,###.##')} ({value.percent.formatNumber('#.#|#.#s')}%)"
                : text;
            }
          };
          configToRender.series[0].slices.adapter = {
            tooltipText: function (text: any, target: any) {
              return target.dataItem.category == 'Other'
                ? showAsPercentage
                  ? "{category} : [bold]{value.formatNumber('#,###.##')}%"
                  : "{category} : [bold]{value.formatNumber('#,###.##')} ({value.percent.formatNumber('#.#|#.#s')}%)"
                : text;
            }
          };
          configToRender.series[0].slices.tooltipText = showAsPercentage
            ? `${getDateFormatForTooltip(
                activeDateGroup as any,
                true
              )}: [bold]{value.formatNumber('#,###.##')}%`
            : `${getDateFormatForTooltip(
                activeDateGroup as any,
                true
              )}: [bold]{value.formatNumber('#,###.##')} ({value.percent.formatNumber('#.#|#.#s')}%)`;
        } else if (!isSegmented && chartType === 'Line') {
          configToRender.series[0].tooltipText = showAsPercentage
            ? `${getDateFormatForTooltip(
                activeDateGroup as any
              )}: [bold]{valueY.formatNumber('#,###.##')}%`
            : `${getDateFormatForTooltip(
                activeDateGroup as any
              )}: [bold]{valueY.formatNumber('#,###.##')} ({valueY.percent.formatNumber('#.#|#.#s')}%)`;
        }

      const finalColorSet =
        chartType === 'PIE'
          ? modifiedConfig.series[0].colors.list
          : modifiedConfig?.colors?.list;
      const existingColorSetLength =
        chartType === 'PIE'
          ? modifiedConfig.series[0].colors.list.length
          : modifiedConfig?.colors?.list?.length;

      const multicolorCharts = !theme_connection_status?.multicolor_charts
        ? multicolor_charts
        : projectVisualSettings?.multicolor_charts;

      if (finalColorSet && finalColorSet?.length < filteredData.length + 1) {
        const colorSet = new am4core.ColorSet();

        for (
          let i = 0;
          i < filteredData.length + 1 - existingColorSetLength;
          i++
        )
          finalColorSet.push(colorSet.next());
      }

      if (
        isSegmented &&
        !isProjectBoard &&
        (chartType === 'Column' || chartType === 'Bar')
      )
        configToRender.series.forEach((series: any) => {
          series.cursorTooltipEnabled = true;
          series.tooltipText = series?.columns?.tooltipText;
        });

      if ((chartType === 'Column' || chartType === 'Bar') && !isSegmented)
        configToRender.series.forEach((series: any, index: any) => {
          series.columns = {
            template: {
              ...series.columns,
              tooltipX: chartType === 'Bar' ? '100%' : '50%',
              tooltipY: chartType === 'Bar' ? '50%' : '0%',
              tooltipText: isDate
                ? `${getDateFormatForTooltip(
                    activeDateGroup as any
                  )} : [bold]{valueY.formatNumber('#,###.##')}[/]${
                    showAsPercentage
                      ? '[bold]%'
                      : `[bold] ({valueY.percent.formatNumber('#.##|#.##s')}%)[/]`
                  }`
                : series?.columns?.template?.tooltipText ??
                  series?.columns?.tooltipText,
              width: autoGroupNumbers && isNumber ? '100%' : '80%',
              height:
                autoGroupNumbers && isNumber
                  ? '100%'
                  : chartType === 'Bar'
                  ? '80%'
                  : '100%',
              adapter: {
                fill: function (fill: any, target: any) {
                  const colors = modifiedConfig.colors;

                  const fillIndex = target.dataItem.index;

                  if (multicolorCharts) {
                    target.stroke = am4core.color('#FFFF');

                    if (
                      !theme_connection_status?.gradient_charts
                        ? gradient_charts
                        : projectVisualSettings?.gradient_charts
                    ) {
                      const gradient = new am4core.LinearGradient();

                      gradient.addColor(
                        finalColorSet[fillIndex % finalColorSet.length],
                        0.5
                      );
                      gradient.addColor(
                        finalColorSet[fillIndex % finalColorSet.length],
                        1
                      );
                      gradient.rotation = -90;

                      return gradient;
                    } else {
                      return finalColorSet[fillIndex % finalColorSet.length];
                    }
                  } else {
                    target.stroke =
                      autoGroupNumbers && isNumber
                        ? am4core.color('#000000')
                        : am4core.color('#FFFFFF');
                    target.strokeOpacity = 0.2;

                    if (
                      !theme_connection_status?.gradient_charts
                        ? gradient_charts
                        : projectVisualSettings?.gradient_charts
                    ) {
                      const gradient = new am4core.LinearGradient();

                      gradient.addColor(
                        colors.list[index % colors.list.length],
                        0.5
                      );
                      gradient.addColor(
                        colors.list[index % colors.list.length],
                        1
                      );

                      gradient.rotation = -90;

                      return gradient;
                    } else {
                      return colors.list[index % colors.list.length];
                    }
                  }
                },
                tooltipHTML: function (html: any, target: any) {
                  const colors = modifiedConfig.colors;
                  const fillIndex = target.dataItem.index;

                  target.tooltip.getFillFromObject = false;

                  if (multicolorCharts && !isSegmented)
                    target.tooltip.background.fill =
                      colors.list[fillIndex % colors.list.length];
                  else
                    target.tooltip.background.fill =
                      colors.list[index % colors.list.length];

                  const r = target.tooltip.background.fill._value.r;
                  const g = target.tooltip.background.fill._value.g;
                  const b = target.tooltip.background.fill._value.b;

                  const luminance =
                    (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

                  target.tooltip.label.fill =
                    luminance < 0.5 ? '#FFFFFF' : '#000000';

                  return html;
                }
              }
            }
          };
        });

      if (chartType === 'PIE')
        configToRender.series.forEach((series: any) => {
          series.slices = {
            ...series.slices,
            template: {
              cursorOverStyle: am4core.MouseCursorStyle.default,
              tooltipText: series.slices.tooltipText,
              adapter: {
                tooltipHTML: function (html: any, target: any) {
                  if (
                    !theme_connection_status?.gradient_charts
                      ? gradient_charts
                      : projectVisualSettings?.gradient_charts
                  )
                    target.tooltip.background.fill =
                      target.fill.stops?.values?.[0]?.color;

                  if (target.tooltip.background.fill) {
                    const r = target.tooltip.background.fill._value.r;
                    const g = target.tooltip.background.fill._value.g;
                    const b = target.tooltip.background.fill._value.b;

                    const luminance =
                      (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

                    target.tooltip.label.fill =
                      luminance < 0.5 ? '#FFFFFF' : '#000000';
                  }

                  return html;
                }
              }
            }
          };
          series.labels.fontSize = getSvg ? 14 : 12;
        });

      if (chartType !== 'PIE' && getSvg && chartType !== 'Bar')
        configToRender.xAxes[0].renderer.labels.template.adapter.push(
          {
            key: 'paddingTop',
            callback: function (padding: any, target: any) {
              return Math.max(
                String(target?.dataItem?.category).length * 1.5,
                20
              );
            }
          },
          {
            key: 'paddingRight',
            callback: function (padding: any, target: any) {
              return String(target?.dataItem?.category).length * 1.5;
            }
          }
        );
    }

    const onChartCreationCallback = (chart: any) => {
      toggleStackChart();

      // if (
      //   filters?.type !== 'none' &&
      //   !isDate &&
      //   filters?.xAxisLabelsExcluded &&
      //   data?.length - filters?.xAxisLabelsExcluded?.length !==
      //     filters?.range.endInd
      // )
      //   setIsLoading(true);
      // else
      setTimeout(() => {
        setIsLoading(false);
        if (setReloadDataTable) setReloadDataTable(false);
      }, 500);

      if (chart.zoomOutButton) chart.zoomOutButton.disabled = true;

      let svgImg;

      const legendDiv = document.getElementById(`legend-div-${chartRefId}`);

      const additionalValue = isPreview ? 20 : 0;

      if (legendDiv)
        legendDiv.style.height =
          chart.legend.contentHeight + additionalValue + 'px';

      if (getSvg || renderAsSvg)
        setTimeout(async () => {
          const hasLegends = !isSegmented ? false : showLegends;
          let legendSvg = '';
          const color = colorSet?.[0] || '#4287f5';

          if (hasLegends && isPreview && legendContainer) {
            legendSvg = await legendContainer.exporting.getSVG(
              'svg',
              {},
              false
            );
            legendSvg = Base64.toBase64(legendSvg);
          }
          if (
            chartType === 'KPI' &&
            typeof kpiTarget !== 'undefined' &&
            kpiTarget !== null
          ) {
            legendSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="643px" height="400px">
            <g fill="${color}" font-family="Arial, sans-serif" font-size="80">
              <text x="50" y="50" dominant-baseline="middle">${roundToNearestMagnitude(
                totalDataPoints
              )}</text>
            </g>
            <g  font-family="Arial, sans-serif" font-size="20">
            <text x="0" y="90" dominant-baseline="middle" fill="${
              colors.dark.main
            }">${getChartOperation(operationType || '')}</text>
          </g>
            </svg>`;

            legendSvg = Base64.toBase64(legendSvg);
          }

          if (
            (chartType === 'KPI' && typeof kpiTarget === 'undefined') ||
            kpiTarget === null
          )
            svgImg = `<svg xmlns="http://www.w3.org/2000/svg" width="643px" height="400px">
            <g fill="${color}" font-family="Arial, sans-serif" font-size="80">
              <text x="250" y="50" dominant-baseline="middle">${roundToNearestMagnitude(
                totalDataPoints
              )}</text>
            </g>
            <g  font-family="Arial, sans-serif" font-size="20">
            <text x="0" y="70" dominant-baseline="middle" fill="${
              colors.dark.main
            }">${getChartOperation(operationType || '')}</text>
          </g>
            </svg>
        `;
          else svgImg = await chart.exporting.getSVG('svg', {}, false);

          if (renderAsSvg) {
            setRenderedSvg(svgImg);
          } else if (getSvg) {
            svgImg = Base64.toBase64(svgImg);

            legendSvg ? getSvg(svgImg, legendSvg) : getSvg(svgImg);
          }

          chart.dispose();
          chartRef.current = undefined;
        }, 100);

      handleOnChartReady();
    };

    if (chartType !== 'KPI')
      configToRender.series = configToRender.series.map((series: any) => ({
        ...series,
        cursorTooltipEnabled: !(isProjectBoard && chartType !== 'Line'),
        type:
          ['ColumnSeries', 'PieSeries'].includes(series.type) &&
          (!theme_connection_status?.['3d_charts']
            ? visualSettings?.['3d_charts']
            : projectVisualSettings?.['3d_charts'])
            ? series.type + '3D'
            : series.type
      }));

    if (needDequeuedChart)
      createDequeueChart(configToRender, onChartCreationCallback);
    else createDeferredChart(configToRender, onChartCreationCallback);
  }, 1000);

  useEffect(() => {
    if (filteredData && modifiedConfig && !chartLoading) {
      setIsLoading(true);
      renderChart();
    }
  }, [
    chartId,
    modifiedConfig,
    chartType,
    filteredData,
    chartLoading,
    multicolor_charts,
    gradient_charts,
    autoGroupNumbers,
    use3DCharts,
    theme_connection_status
  ]);

  useEffect(() => {
    if (filteredData && modifiedConfig && !chartLoading && isProjectBoard) {
      setIsLoading(true);
      renderChart();
    }
  }, [projectVisualSettings]);

  // useEffect(() => {
  //   if (filteredData && modifiedConfig && isProjectBoard) {
  //     setIsLoading(true);
  //     renderChart();
  //   }
  // }, [projectBoard.data]);

  useEffect(() => {
    toggleDataLabels();
  }, [showDataLabels, chartId, chartRef?.current]);

  useEffect(() => {
    toggleStackChart();
  }, [stackChart, chartRef.current]);

  useEffect(() => {
    toggleGradient();
  }, [chartId, chartRef.current, gradient_charts]);

  // hook to update Line charts styles
  useEffect(() => {
    if (chartRef.current)
      if (chartType === 'Line')
        chartRef.current?.series?.values?.forEach((series: any) => {
          if (series.dataItems.values.length === 1 && series?.bullets) {
            series?.bullets?.push(new CircleBullet());
            series.bullets.template.radius = 5;
            chartRef.current.maskBullets = false;
          }
        });
  }, [chartId, chartRef.current, data, visualSettings]);

  useEffect(
    () => () => {
      if (chartRef?.current) chartRef.current.dispose();
    },
    []
  );

  const isNotTimeSeries = isShareView
    ? (currentChartPreviewData.config?.date_group === 'none' ||
        !currentChartPreviewData.config?.date_group) &&
      (currentChartPreviewData.config?.x_axis?.treat_as_type === 'DATE' ||
        currentChartPreviewData.config?.x_axis?.treat_as_type === 'DATETIME')
    : (currentChartData.config?.date_group === 'none' ||
        !currentChartData.config?.date_group) &&
      (xAxisType === 'DATE' || xAxisType === 'DATETIME');

  const chartTitle = isShareView
    ? currentChartPreviewData.chart_title
    : isPreview || isProjectBoard
    ? title
    : currentChartData.chart_title;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [fontSize, setFontSize] = useState(35);

  useEffect(() => {
    const w = size?.w || 1;
    const h = size?.h || 1;

    if (h > w) setFontSize(35 * w);
    else setFontSize(35 * h);
  }, [size]);

  const typographyRef = useRef<HTMLDivElement | null>(null);

  if (!chartId) return null;

  function createNumberFormatter(isxAxis: boolean) {
    const numberFormatter = new am4core.NumberFormatter();

    if (isxAxis)
      numberFormatter.numberFormat = isXPercentage ? '#.##p' : '#.#a';
    else numberFormatter.numberFormat = showAsPercentage ? '#.##p' : '#.#a';

    numberFormatter.bigNumberPrefixes = [
      { number: 1e3, suffix: 'K' },
      { number: 1e6, suffix: 'M' },
      { number: 1e9, suffix: 'B' },
      { number: 1e12, suffix: 'T' },
      { number: 1e15, suffix: 'P' },
      { number: 1e18, suffix: 'E' },
      { number: 1e21, suffix: 'Z' },
      { number: 1e24, suffix: 'Y' },
      { number: 1e27, suffix: 'R' },
      { number: 1e30, suffix: 'Q' }
    ];
    numberFormatter.smallNumberPrefixes = [];

    return numberFormatter;
  }

  return (
    <>
      {(isLoading || chartLoading) && (
        <Grid
          container
          width={width}
          height={'40px'}
          position='absolute'
          justifyContent='center'
          top='50%'
          display={isPreview && chartType === 'KPI' ? 'none' : undefined}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
      {isProjectBoard ? (
        chartType !== 'KPI' ||
        (chartType === 'KPI' &&
          typeof kpiTarget !== 'undefined' &&
          kpiTarget !== null) ? (
          <div
            style={{
              width,
              height,
              opacity: isLoading || chartLoading ? 0 : 1
            }}
          >
            <Tooltip
              title={
                chartType === 'KPI'
                  ? `${chartTitle} : ${getChartOperation(
                      chartOperation
                    )} ( ${totalDataPoints.toLocaleString()} )`
                  : ''
              }
              placement='top'
            >
              <div
                id={`${chartId}${isPreview ? '-preview' : ''}${
                  isProjectBoard ? '-board' : ''
                }`}
                className='chart-container'
                style={{
                  width: width,
                  height: chartType === 'KPI' ? '80%' : height,
                  opacity: isLoading || chartLoading || renderAsSvg ? 0 : 1,
                  transition: 'opacity 0.3 ease-in',
                  fontSize: getSvg ? '0.875rem' : '0.75rem',
                  cursor: isBoardPreview ? 'cursor' : 'default'
                }}
                ref={chartViewRef}
              />
            </Tooltip>
            {renderAsSvg && (
              <div
                dangerouslySetInnerHTML={{ __html: renderedSvg }}
                style={{
                  opacity: isLoading || chartLoading ? 0 : 1,
                  transition: 'opacity 0.3 ease-in',
                  cursor: 'default',
                  maxWidth: '100vw',
                  paddingTop: chartType === 'KPI' ? '20px' : '40px',
                  height,
                  overflow: 'hidden'
                }}
              />
            )}

            {chartType === 'KPI' && (
              <MDTypography
                variant='h1'
                fontSize='35px'
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  height: '20%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: colorSet?.[0] || '#4287f5'
                }}
              >
                {roundToNearestMagnitude(totalDataPoints)}
              </MDTypography>
            )}
          </div>
        ) : (
          <MDBox
            id='text-container'
            sx={{
              width,
              height: mobileView ? '220px' : height,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize,
              opacity: isLoading || chartLoading ? 0 : 1,
              color: colorSet?.[0] || '#4287f5',
              fontWeight: 700
            }}
            ref={containerRef}
          >
            <Tooltip
              title={`${chartTitle} : ${getChartOperation(
                chartOperation
              )} ( ${totalDataPoints.toLocaleString()} )`}
              placement='top'
            >
              <span>{roundToNearestMagnitude(totalDataPoints)}</span>
            </Tooltip>
          </MDBox>
        )
      ) : chartType !== 'KPI' ? (
        <>
          <MDBox
            display='flex'
            flexDirection='column'
            position='relative'
            width='100%'
            id='chartContainer'
            height={
              chartType === 'Bar' && !mobileView
                ? `${
                    6.5 * filteredData?.length < 100
                      ? 100
                      : 6.5 * filteredData?.length
                  }%`
                : '100%'
            }
          >
            {isPreview && chartType === 'Bar' && (
              <div
                id={`legend-div-${chartRefId}`}
                style={{
                  fontSize: '0.75rem',
                  width,
                  display: showLegends && isSegmented ? 'flex' : 'none'
                }}
              />
            )}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              id={`${chartId}${isPreview ? '-preview' : ''}${
                isProjectBoard ? '-board' : ''
              }`}
              className='chart-container'
              style={{
                width,
                height,
                opacity: isLoading || chartLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in',
                cursor: 'default'
              }}
              ref={chartViewRef}
              onClick={() => {
                if (!isPreview && !isShareView)
                  sendAmplitudeData(CLICKED_CHART_AREA);
              }}
            />

            {isPreview && chartType !== 'Bar' && (
              <div
                id={`legend-div-${chartRefId}`}
                style={{
                  fontSize: '0.75rem',
                  width,
                  display: showLegends && isSegmented ? 'flex' : 'none'
                }}
              />
            )}

            {isNotTimeSeries && (
              <MDBox position='absolute' bottom={24}>
                <MDTypography variant='sm' color='grey'>
                  Data is not represented as a time series
                </MDTypography>
              </MDBox>
            )}

            {showWaterMark && !isLoading && !chartLoading && (
              <MDBox position='absolute' top={25} right={15}>
                <Watermark
                  brandLogo={brandLogo}
                  isKPI={chartType === 'KPI'}
                  isPreview={isPreview}
                  onClickRemoveWaterMark={onClickRemoveWaterMark}
                />
              </MDBox>
            )}
          </MDBox>
        </>
      ) : (
        <>
          <div
            style={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: isLoading || chartLoading ? 0 : 1,
              position: 'relative'
            }}
          >
            {showWaterMark && !isLoading && !chartLoading && (
              <MDBox
                position='absolute'
                top={isPreview ? -35 : mobileView ? 5 : 25}
                right={isPreview ? 10 : mobileView ? 5 : 35}
              >
                <Watermark
                  brandLogo={brandLogo}
                  isKPI={chartType === 'KPI'}
                  isPreview={isPreview}
                  onClickRemoveWaterMark={onClickRemoveWaterMark}
                />
              </MDBox>
            )}

            <MDBox
              display='flex'
              flexDirection='column'
              bordered
              width={
                mobileView
                  ? '100%'
                  : typographyRef.current &&
                    typographyRef.current?.offsetWidth > 500
                  ? typographyRef.current?.offsetWidth + 200
                  : '600px'
              }
              height='fit-content'
              sx={({ palette }) => ({
                borderColor: palette.secondary.main
              })}
            >
              {!isPreview && (
                <MDTypography
                  variant='h1'
                  fontSize={mobileView ? '25px' : '2rem'}
                  color='secondary'
                  sx={{ padding: '10px' }}
                >
                  {formatTitle(chartTitle || '')}
                </MDTypography>
              )}
              {typeof kpiTarget !== 'undefined' && kpiTarget !== null && (
                <>
                  <Tooltip
                    title={`${chartTitle} : ${getChartOperation(
                      chartOperation
                    )} ( ${totalDataPoints.toLocaleString()} )`}
                    placement='top'
                  >
                    <div
                      id={`${chartId}${isPreview ? '-preview' : ''}${
                        isProjectBoard ? '-board' : ''
                      }`}
                      className='chart-container'
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: mobileView ? '200px' : '180px',
                        opacity: isLoading || chartLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in',
                        cursor: 'default'
                      }}
                      ref={chartViewRef}
                    />
                  </Tooltip>
                </>
              )}
              <MDBox
                sx={{
                  height: mobileView ? '50px' : '100px',
                  width: '100%',
                  display: 'flex',
                  mb: 0.5,
                  position: 'relative'
                }}
              >
                <MDTypography
                  variant='caption'
                  color='info'
                  paddingLeft='10px'
                  sx={{
                    position: 'absolute',
                    bottom: mobileView ? 5 : 20,
                    left: 0
                  }}
                >
                  {getChartOperation(chartOperation) || ''}
                </MDTypography>
                <MDBox
                  sx={{
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <Tooltip
                    title={`${chartTitle} : ${getChartOperation(
                      chartOperation
                    )} ( ${totalDataPoints.toLocaleString()} )`}
                    placement='top'
                  >
                    <MDTypography
                      variant='h1'
                      fontSize={mobileView ? '30px' : '80px'}
                      height='fit-content'
                      ref={typographyRef}
                      sx={{
                        cursor: 'default',
                        color: colorSet?.[0] || '#4287f5'
                      }}
                    >
                      {roundToNearestMagnitude(totalDataPoints)}
                    </MDTypography>
                  </Tooltip>
                </MDBox>
              </MDBox>
            </MDBox>
            {/* </MDBox> */}
          </div>
        </>
      )}
    </>
  );
};

export default Chart;
